import { useState, useEffect } from 'react';
import { X, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useCart } from '../context/CartContext';
import { useVouchers } from '../context/VoucherContext';
import { formatPrice } from '../data/products';
import { Link } from 'react-router-dom';
import CTAButton from './CTAButton';
import { calculateVoucherDiscount } from '../utils/cartUtils';
import { EmptyCart, CartItem, AppliedVoucher, CartSummary, VoucherSelector } from './CartDrawerComponents';
import { VoucherDrawer } from './VoucherDrawer';

export default function CartDrawer() {
  const { items, isOpen, setCartOpen, removeItem, updateQuantity, totalItems, subtotal } = useCart();
  const { appliedVoucher, removeVoucher } = useVouchers();
  const [isVoucherDrawerOpen, setIsVoucherDrawerOpen] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    if (isOpen) {
      html.classList.add('no-scroll');
    } else {
      html.classList.remove('no-scroll');
    }
    return () => html.classList.remove('no-scroll');
  }, [isOpen]);

  const discount = calculateVoucherDiscount(appliedVoucher, subtotal);
  const finalTotal = subtotal - discount;
  const isMinPurchaseMet = !appliedVoucher || subtotal >= (appliedVoucher.conditions?.min_purchase || 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#5A252D]/35 backdrop-blur-sm z-[60]"
            onClick={() => setCartOpen(false)}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-paper z-[70] flex flex-col shadow-2xl border-l border-ink/5"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-6 border-b border-ink/5">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-brand-wine" />
                <h2 className="text-xl font-serif text-ink tracking-tight">
                  Keranjang <span className="text-sm font-sans font-medium text-muted/60 ml-1">({totalItems})</span>
                </h2>
              </div>
              <button
                onClick={() => setCartOpen(false)}
                className="w-10 h-10 flex items-center justify-center hover:bg-ink/5 transition-colors"
              >
                <X className="w-5 h-5 text-ink" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4" data-lenis-prevent>
              {items.length === 0 ? (
                <EmptyCart onContinueShopping={() => setCartOpen(false)} />
              ) : (
                <div className="flex flex-col gap-4">
                  {items.map((item) => (
                    <CartItem
                      key={item.sku}
                      item={item}
                      updateQuantity={updateQuantity}
                      removeItem={removeItem}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-6 py-6 border-t border-ink/5 bg-white">
                <VoucherSelector
                  appliedVoucher={appliedVoucher}
                  onOpen={() => setIsVoucherDrawerOpen(true)}
                />

                <AnimatePresence>
                  {appliedVoucher && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <AppliedVoucher
                        appliedVoucher={appliedVoucher}
                        isMinPurchaseMet={isMinPurchaseMet}
                        discount={discount}
                        subtotal={subtotal}
                        removeVoucher={removeVoucher}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <CartSummary
                  subtotal={subtotal}
                  discount={discount}
                  finalTotal={finalTotal}
                />

                <p className="text-[10px] text-[#3A3A3A]/50 mt-4 leading-relaxed">
                  Ongkir dihitung saat checkout.
                </p>

                <CTAButton
                  to="/checkout"
                  onClick={() => setCartOpen(false)}
                  variant="wine"
                  className="w-full mt-2"
                >
                  Lanjutkan ke Checkout
                </CTAButton>

                <VoucherDrawer
                  isOpen={isVoucherDrawerOpen}
                  onClose={() => setIsVoucherDrawerOpen(false)}
                />
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
